import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AuthenticationService } from '../../../auth/authentication.service';
import { CFDocumentToDisplay } from '../../../models/cf-document/cf-document-to-display';
import { CFSectionToDisplay } from '../../../models/cf-document/cf-section-to-display';
import { KeyValuePairEnum } from '../../../models/key-value-pair-enum';
import { CFParticipant } from '../../../models/participants/cf-participant';
import { CFParticipantToDisplay } from '../../../models/participants/cf-participant-to-display';
import { CFSignatureForInit } from '../../../models/signatures/cf-signature-for-init';
import { CFDocumentStoreService } from '../../../services/documents/cf-document-store.service';
import { CFSignatureStoreService } from '../../../services/signatures/cf-signature-store.service';
import { CFSignatureService } from '../../../services/signatures/cf-signature.service';

@UntilDestroy()
@Component({
    selector: 'clfl-signatures',
    templateUrl: './signatures.component.html',
    styleUrls: ['./signatures.component.scss']
})
export class SignaturesComponent implements OnInit {
    @Input() public disabled = false;
    @Input() public unsignedSections: Array<CFSectionToDisplay> = [];
    @Input() public participants: Array<CFParticipantToDisplay> = [];
    @Output() public readonly onSign = new EventEmitter<void>();
    @Output() public readonly onLoading = new EventEmitter<boolean>();

    public document!: CFDocumentToDisplay;
    public signersPerSection: Array<CFParticipantToDisplay> = [];
    public signatureTypes: Array<KeyValuePairEnum> = [];

    constructor(
        private documentStoreService: CFDocumentStoreService,
        public signatureStoreService: CFSignatureStoreService,
        private signatureService: CFSignatureService,
        private authenticationService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.signatureStoreService.signatureTypes$.pipe(untilDestroyed(this)).subscribe({
            next: (types) => (this.signatureTypes = types)
        });

        this.documentStoreService.documentData$.pipe(untilDestroyed(this)).subscribe({
            next: (document) => {
                this.document = document;

                if (this.unsignedSections && this.unsignedSections.length > 0) {
                    this.selectSection(this.unsignedSections[0].sectionId);
                }
            }
        });
    }

    public initiateSignature() {
        this.onLoading.emit(true);
        const requester = new CFParticipant(
            this.authenticationService.currentUserValue.userResource.id.toString(),
            this.authenticationService.currentUserValue.userResource.email,
            this.authenticationService.currentUserValue.userResource.userName
        );

        const participant = this.signersPerSection.find((s) => s.participantId === this.signatureStoreService.selectedSignerId);
        const signer = new CFParticipant(participant.participantId, participant.email, participant.username);
        const signatureForInit = new CFSignatureForInit(this.signatureStoreService.selectedSignatureType, requester, signer);

        this.signatureService
            .initiateSignature(this.document.cloudFlowId, this.signatureStoreService.selectedSectionId, signatureForInit)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (data: any) => {
                    this.signatureStoreService.signatureId = data.signatureId;
                    this.onLoading.emit(false);
                    this.onSign.emit();
                },
                error: (err: any) => {
                    this.onLoading.emit(false);
                }
            });
    }

    public selectSection(newSectionId: string) {
        const selectedSection = this.unsignedSections.find((s) => s.sectionId === newSectionId);

        this.signatureStoreService.selectedSectionId = newSectionId;
        this.signatureStoreService.signatureId = selectedSection?.signatureId;
        this.signatureStoreService.selectedSignatureType = selectedSection.signatureType;

        this.signersPerSection = this.participants.filter((participant) => participant.sectionId === newSectionId && participant.canSign);

        if (selectedSection.signerEmail) {
            this.signatureStoreService.selectedSignerId = this.signersPerSection.find((s) => s.email === selectedSection.signerEmail)
                ?.participantId;
        } else {
            this.signatureStoreService.selectedSignerId = this.signersPerSection[0]?.participantId;
        }
    }
}
